import { Bike, Route } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { Button, Dialog, DialogContent, DialogFooter, Field } from '@aeris/ui'

import { PartyPicker } from './party-picker.tsx'

/**
 * Quem vai levar (F034).
 *
 * Uma pergunta só, porque é uma pergunta só: o motoboy. O que acontece em
 * seguida — a mercadoria sair do físico e nascer um título a receber — não é
 * escolha de ninguém no balcão, é o que despachar significa, e por isso está
 * escrito aqui em vez de virar mais um campo.
 *
 * A busca é entre pessoas com papel `courier` (F098), e não `carrier`: o
 * motoboy da casa e a transportadora de fora são coisas diferentes, e quem faz
 * frete aparecendo no acerto do motoboy é a confusão que o papel separado
 * desfez. Um cliente escolhido por engano apareceria devendo um dinheiro que
 * nunca teve na mão — o servidor recusa, e esta lista faz o engano não
 * acontecer.
 */

export type CourierDialogProps = {
  title: string
  pending: boolean
  /**
   * O que o servidor recusou, dentro do diálogo.
   *
   * Ficava na página, atrás da máscara do modal: despachar falhava, a pessoa
   * continuava olhando o mesmo diálogo aberto e **nada** acontecia na tela
   * (F069). O diálogo também não se fecha sozinho no erro — e não deve —,
   * então o único lugar onde a frase é lida é aqui dentro.
   */
  error?: string | null
  onChoose: (courierPartyId: string) => void
  /**
   * "Entregar na rota" (F266): o pedido não sai agora, espera na fila até o
   * caixa montar a saída em Rotas de entrega. Ausente na conta sem a chave
   * `entregas-rota` — aí o botão nem aparece.
   */
  onQueue?: (() => void) | undefined
  onClose: () => void
}

export const CourierDialog = ({
  title,
  pending,
  error = null,
  onChoose,
  onQueue,
  onClose,
}: CourierDialogProps): ReactNode => {
  const [courierPartyId, setCourierPartyId] = useState('')

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title={title}
        description="A mercadoria sai agora e o dinheiro chega depois: o pedido gera um título a receber, acertado na tela do motoboy."
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (courierPartyId !== '' && !pending) onChoose(courierPartyId)
          }}
        >
          <Field
            label="Entregador"
            hint="Pessoas com o papel de entregador no cadastro."
          >
            {(field) => (
              <PartyPicker
                {...field}
                role="courier"
                value={courierPartyId}
                placeholder="Buscar o entregador pelo nome"
                onChange={setCourierPartyId}
              />
            )}
          </Field>

          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            {onQueue === undefined ? null : (
              <Button
                type="button"
                variant="secondary"
                disabled={pending}
                leadingIcon={<Route aria-hidden="true" className="h-4 w-4" />}
                onClick={onQueue}
              >
                Entregar na rota
              </Button>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={courierPartyId === '' || pending}
              leadingIcon={<Bike aria-hidden="true" className="h-4 w-4" />}
            >
              {pending ? 'Despachando…' : 'Despachar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
